import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- Theme Variables ---
const PrimaryColor = '#3f51b5'; // A professional blue
const SecondaryColor = '#ff5722'; // The original red-orange for accents/buttons
const BackgroundLight = '#f8f9fa';
const BackgroundDark = '#212529'; // Darker for high contrast
const TextDark = '#333333';
const TextLight = '#ffffff';

// --- Global Styles to Reset and Apply Base Font/Color ---

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap');

  body {
    margin: 0;
    padding: 0;
    font-family: 'Roboto', sans-serif;
    color: ${TextDark};
    line-height: 1.6;
    background-color: ${BackgroundLight};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

// --- Styled Components for the entire page structure ---

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
`;


const Section = styled.section`
  padding: 80px 5%;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
  @media (min-width: 1024px) {
    padding: 100px 5%;
  }
`;

const DarkSection = styled(Section)`
  background-image: url('https://via.placeholder.com/1600x900?text=Dark+Section+Background+Image');
  background-size: cover;
  background-position: center;
  background-attachment: fixed; /* Improved: Parallax effect */
  background-color: ${BackgroundDark};
  color: ${TextLight};
  padding: 100px 5%;
  text-align: left;
  position: relative;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8); /* Stronger overlay */
    z-index: -1;
  }
`;

const Footer = styled.footer`
  background-color: #e9ecef; /* Lighter footer background */
  padding: 50px 5%;
  text-align: center;
  color: ${TextDark};
  font-size: 0.9em;
  @media (min-width: 1024px) {
    padding: 60px 10%;
  }
`;

// --- Styled Components for common elements ---

const Title = styled.h2`
  font-size: 2.2em;
  color: ${props => props.dark ? TextLight : PrimaryColor};
  margin-bottom: 25px;
  line-height: 1.2;
  font-weight: 700;
  @media (min-width: 768px) {
    font-size: 3em;
  }
`;

const Subtitle = styled.h3`
  font-size: 1.8em;
  margin-bottom: 20px;
  color: ${props => props.dark ? TextLight : TextDark};
  text-transform: capitalize;
  font-weight: 400;
  @media (min-width: 768px) {
    font-size: 2.2em;
  }
`;

const Paragraph = styled.p`
  max-width: 800px;
  margin: 0 auto 20px auto;
  color: ${props => props.dark ? '#cccccc' : '#555555'};
  font-size: 1.1em;
  font-weight: 300;
`;

const Button = styled.a`
  display: inline-block;
  background-color: ${SecondaryColor};
  color: ${TextLight};
  padding: 12px 40px;
  margin: 30px 10px 0;
  text-decoration: none;
  font-weight: 700;
  border-radius: 50px; /* Pill-shaped button */
  transition: all 0.3s ease;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 0.95em;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(255, 87, 34, 0.4); /* Subtle glow effect */

  &:hover {
    background-color: #e64a19;
    transform: translateY(-2px); /* Lift on hover */
    box-shadow: 0 6px 20px rgba(255, 87, 34, 0.6);
  }
`;

// --- Section Specific Styled Components ---

// 1. Hero Section
const HeroSection = styled(Section)`
  padding: 80px 5% 60px;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 40px;
  align-items: center;
  justify-content: center;
  background-color: ${TextLight};
  border-bottom: 5px solid ${PrimaryColor}; /* Stronger visual separation */

  @media (min-width: 768px) {
    flex-direction: row;
    text-align: left;
    padding: 120px 5% 80px;
  }
`;

const HeroText = styled.div`
  flex: 1;
  max-width: 600px;
`;

const HeroImage = styled.div`
  /* Placeholder for the monitor/lollipop image */
  width: 100%;
  max-width: 400px;
  height: 300px;
  background: url('https://via.placeholder.com/400x300?text=Web+Traffic+Secrets') no-repeat center center / cover;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15); /* Modern image presentation */
  border: 5px solid ${PrimaryColor};
  @media (min-width: 768px) {
    width: 400px;
  }
`;

// 2. Can Web Analytics boost your sales?
const BoostSection = styled(Section)`
  background-color: ${BackgroundLight};
  padding-top: 60px;
  padding-bottom: 60px;
`;

// 3. How does web analytics improve critical performance?
const PerformanceSection = styled(Section)`
  background-color: ${TextLight}; /* Changed to white for better contrast with blocks */
  padding: 80px 5%;
`;

const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  max-width: 1100px;
  margin: 50px auto 0;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PerformanceBlock = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${BackgroundLight}; /* Slight contrast */
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.15);
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    flex-direction: row;
    text-align: left;
  }
  @media (min-width: 1024px) {
    flex-direction: column;
  }
`;

const BlockImage = styled.div`
  width: 100%;
  height: 200px; /* Taller image for impact */
  background: url(${props => props.bg}) no-repeat center center / cover;
  
  @media (min-width: 768px) and (max-width: 1023px) {
    width: 200px;
    height: auto;
    flex-shrink: 0;
  }
  @media (min-width: 1024px) {
    height: 180px;
  }
`;

const BlockContent = styled.div`
  flex: 1;
  padding: 25px;
`;

const BlockTitle = styled.h4`
  font-size: 1.3em;
  color: ${PrimaryColor};
  margin: 0 0 10px 0;
  font-weight: 700;
`;

// 4. Why Choose Us (Dark Section)
const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 25px;
  margin-top: 50px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ChoiceBlock = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.05); /* Very subtle white background */
  border-radius: 8px;
  text-align: left;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
  
  /* Make the 5th block span across */
  ${props => props.fullWidth && `
    @media (min-width: 1024px) {
      grid-column: 1 / -1; 
      max-width: 800px;
      margin: 0 auto;
      text-align: center;
    }
  `}
`;

const ChoiceTitle = styled.h4`
  color: ${SecondaryColor};
  font-size: 1.4em;
  margin-bottom: 10px;
  font-weight: 700;
`;

// 5. Why is analytics necessary?
const NecessitySection = styled(Section)`
  padding: 80px 5%;
  background-color: ${BackgroundLight};
`;

const NecessityList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 800px;
  margin: 50px auto;
  text-align: left;
`;

const NecessityItem = styled.li`
  display: flex;
  align-items: center; /* Center item vertically */
  margin-bottom: 25px;
  gap: 20px;
  border-bottom: 1px solid #eeeeee; /* Separator */
  padding-bottom: 15px;
`;

const ItemNumber = styled.div`
  width: 40px;
  height: 40px;
  background-color: ${PrimaryColor};
  border-radius: 50%;
  color: ${TextLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2em;
  flex-shrink: 0;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
`;

const ItemContent = styled.div`
  flex: 1;
`;

const ItemTitle = styled.h4`
  margin: 0 0 5px 0;
  font-size: 1.2em;
  color: ${TextDark};
  font-weight: 500;
`;


// --- Main Component ---

const Analytics = () => {
  return (
    <Home>
    <PageContainer>
      <GlobalStyle />

      {/* 1. Header/Hero Section */}
      <HeroSection id="hero">
        <HeroText>
          <Title>Unlocking The True Secrets Of **Web Traffic**</Title>
          <Paragraph>
            Web traffic is more than just clicks and page views. It's the pulse of your online business. Without accurate data, you're navigating blind. **SRK Software** provides the insights needed to understand your visitors, optimize your site, and drive exponential growth.
          </Paragraph>
          <Button href="#get-started">Start Your Free Audit</Button>
        </HeroText>
        <HeroImage />
      </HeroSection>

      {/* 2. Can Web Analytics boost your sales? */}
      <BoostSection>
        <Subtitle>Are you aware that web analytics can fundamentally boost your sales?</Subtitle>
        <Paragraph>
          **Absolutely!** By analyzing visitor behavior, you can identify high-converting paths, see exactly where users drop off, and tailor your content to match customer intent. This targeted approach directly translates into higher conversion rates and increased sales. We give you the tools to succeed.
        </Paragraph>
        <Button href="#learn-more">Learn How</Button>
      </BoostSection>

      {/* 3. How does web analytics improve critical performance? */}
      <PerformanceSection id="performance">
        <Title dark={false}>How Web Analytics **Supercharges** Performance</Title>
        <PerformanceGrid>
          <PerformanceBlock>
            <BlockImage bg="https://via.placeholder.com/400x180?text=Product+Optimization" />
            <BlockContent>
              <BlockTitle>Optimizing Product Performance</BlockTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95em', color: '#555' }}>
                Identify which products or services perform best, understand user engagement, and make data-driven decisions on inventory, pricing, and promotion strategies.
              </Paragraph>
            </BlockContent>
          </PerformanceBlock>
          <PerformanceBlock>
            <BlockImage bg="https://via.placeholder.com/400x180?text=Conversion+Strategies" />
            <BlockContent>
              <BlockTitle>Crafting Conversion Strategies</BlockTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95em', color: '#555' }}>
                Analyze your sales funnel to pinpoint bottlenecks and conversion killers. Use A/B testing insights to refine your path to purchase for maximum efficiency.
              </Paragraph>
            </BlockContent>
          </PerformanceBlock>
          <PerformanceBlock>
            <BlockImage bg="https://via.placeholder.com/400x180?text=Informed+Decisions" />
            <BlockContent>
              <BlockTitle>Informed Decision-Making</BlockTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95em', color: '#555' }}>
                Move beyond guesswork. Web analytics provides objective data on what works and what doesn't, allowing for smarter allocation of resources and marketing spend.
              </Paragraph>
            </BlockContent>
          </PerformanceBlock>
        </PerformanceGrid>
      </PerformanceSection>

      {/* 4. Why Choose Us (Dark Section) */}
      <DarkSection id="choose-us">
        <Subtitle dark style={{ textAlign: 'center' }}>Why **SRK Software** is Your Best Choice</Subtitle>
        <ChoiceGrid>
          <ChoiceBlock>
            <ChoiceTitle>Intuitive Navigation</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Our platform offers a clean, easy-to-use interface, making complex data accessible and actionable for all team members, regardless of technical skill.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Refinement & Sophistication</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Leverage advanced segmentation and custom reporting to dive deeper into niche user groups and uncover subtle trends that competitors miss.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Increased Focus & Clarity</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              By automating routine data collection, we allow your team to spend less time gathering data and more time acting on the insights, improving productivity.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Reliability & Transparency</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Trust your data. Our tracking methods ensure high accuracy, and we provide clear documentation on how metrics are calculated, offering complete transparency.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Exceptional Support</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Benefit from 24/7 dedicated customer support and a wealth of educational resources to ensure you maximize the value of your data.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock fullWidth>
            <ChoiceTitle>Cutting-edge Appeal</ChoiceTitle>
            <Paragraph dark style={{ margin: 0, maxWidth: 'unset' }}>
              We continuously update our tools with the latest in machine learning and AI to give you a predictive edge, helping you anticipate market shifts and customer behavior.
            </Paragraph>
          </ChoiceBlock>
        </ChoiceGrid>
        <div style={{ textAlign: 'center' }}>
          <Button href="#contact">Schedule a Demo Today</Button>
        </div>
      </DarkSection>

      {/* 5. Why is analytics necessary? */}
      <NecessitySection>
        <Subtitle>Why is Web Analytics an Absolute Necessity?</Subtitle>
        <Paragraph>
          In today's digital landscape, understanding your user is paramount. Analytics is the bridge between raw data and massive business growth. It's necessary for:
        </Paragraph>
        <NecessityList>
          <NecessityItem>
            <ItemNumber>1</ItemNumber>
            <ItemContent>
              <ItemTitle>Informed Decision-Making</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '1em', color: '#555' }}>
                Use objective, real-time data instead of intuition to confidently guide all your marketing and development efforts.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>2</ItemNumber>
            <ItemContent>
              <ItemTitle>Understanding Audience Behavior</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '1em', color: '#555' }}>
                See exactly what your visitors are doing, what they like, and what drives them away from your critical funnels.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>3</ItemNumber>
            <ItemContent>
              <ItemTitle>Measuring Performance</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '1em', color: '#555' }}>
                Track the success of campaigns, content, and site changes with quantifiable metrics like **ROI** and **Conversion Rate**.
              </Paragraph>
            </ItemContent>
           </NecessityItem>
          <NecessityItem>
            <ItemNumber>4</ItemNumber>
            <ItemContent>
              <ItemTitle>Optimizing Strategies</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '1em', color: '#555' }}>
                Continuously test and refine your marketing and content strategies based on rapid, real-time user feedback loops.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>5</ItemNumber>
            <ItemContent>
              <ItemTitle>Unveiling Opportunities</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '1em', color: '#555' }}>
                Identify untapped markets, potential product gaps, and new, high-value traffic sources that can fuel future growth.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
        </NecessityList>
        <Button href="#start-analysis">Start Analysis Now</Button>
      </NecessitySection>

    </PageContainer>
    </Home>
  );
};

export default Analytics;

